// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../common/api';
import alert from '../../../share/alert';
import config from '../../../share/config';
import cstmTable from '../../../share/components/vue/PaginationTable';
import loading from '../../../share/components/vue/ProcessingIcon';
import individualDiagnose from '../../../share/util/individualDiagnose';
import util from '../../../share/util/utils';
import cancelCodeResult from '../../vue/IndividualCancelCodeResult';
import eventLogUtil from '../../../share/util/eventLogUtil';
import DiagSystemUtils from '../../../common/nodejs/diagSystemUtils';
import { getAbbrSysName } from '../../../share/util/systemName';

export default {
  name: 'individualCancelCode',
  data() {
    return {
      systemId: undefined,
      processing: false,
      /** APIから取得した機能リストを常に保持しておく */
      originalCancelCodeList: [],
      /** 画面に表示する為の機能リスト */
      filteredCancelCodeList: [],
      /** 選択中のキャンセルコード */
      selectedCancelCode: {},
      /** フィルター用テキスト */
      filterText: '',
      menuResponse: undefined,
      i18nCncKey: undefined,
      cancelCodeId: undefined,
      showCancelCodeResult: false,
      // システム間移動判定
      isUseCache: true,
      functionList: []
    };
  },

  props: {
    // noop
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'cancel-code-result': cancelCodeResult
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.menuResponse) {
          return;
        }

        const MenuList = this.getCnci18nMenuList(this.menuResponse, this.systemId);
        this.filteredCancelCodeList = MenuList;
        this.originalCancelCodeList = MenuList;
        this.updateFilter();
      }
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      isPopstateNavigation: state => state.cache.isPopstateNavigation,
    }),

    systemTableColumns() {
      return [
        { name: 'menu_title', label: 'individual.header_work_support_table_item' },
      ];
    }
  },

  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      vm.beforeRouteProcess(vm, from);
    });
  },

  /**
   * 遷移してきた画面のパラメータ情報を保持
   * 同一コンポーネントが再帰的に利用された場合、呼び出される
   */
  beforeRouteUpdate(to, from, next) {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      next();
      return;
    }
    this.$nextTick(() => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      this.beforeRouteProcess(this, from);
      this.originalCancelCodeList = [];
      this.filteredCancelCodeList = [];
      this.selectedCancelCode = {};
      this.showCancelCodeResult = false;
      // mounted時に行う共通処理を呼び出す
      this.mountedLogic();
    });
    next();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('cache', ['cacheIsPopstateNavigation']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.updateTable();
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow: function (selected) {
      this.selectedCancelCode = selected;
      this.setHighLight();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filteredCancelCodeList.forEach(item => {
        if (item.cancel_code_id === this.selectedCancelCode.cancel_code_id) {
          item.fieldSpec = {};
          item.fieldSpec.menu_title = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
          }
        }
      });
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilter() {
      this.selectedCancelCode = {};
      this.setHighLight();
      this.filteredCancelCodeList = this.originalCancelCodeList.filter(
        // 翻訳後の名称を大文字・小文字区別なくフィルターする
        item => item.menu_title.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },

    /** キャンセルコード選択時 */
    onClickSelect() {
      // 選択中のキャンセルコードがない場合、return
      if (!this.selectedCancelCode.cancel_code_id) {
        return;
      }

      // キャンセルコード一覧用のi18nKeyの取得
      this.i18nCncKey = `cnc${this.systemId}.${this.selectedCancelCode.cancel_code_id}`;

      this.cancelCodeId = this.selectedCancelCode.cancel_code_id;
      // アイサイトの場合、サポートされている機能IDリストを詳細画面に渡す
      if (DiagSystemUtils.isEyesightByAbbreviationId(getAbbrSysName(this.systemId))) {
        this.functionList = this.menuResponse.filter(e => DiagSystemUtils.isEyesightByAbbreviationId(getAbbrSysName(e.requirement_id))).map(obj => ({
          id: obj.cancel_code_id,
          sort_id: obj.sort_id
        }));
      }
      // 統合ECUの場合、サポートされている機能IDリストを詳細画面に渡す
      if (DiagSystemUtils.isAdasEcuByAbbreviationId(getAbbrSysName(this.systemId))) {
        this.functionList = this.menuResponse.map(obj => ({
          id: obj.cancel_code_id,
          sort_id: obj.sort_id
        }));
      }
      this.showCancelCodeResult = true;
    },

    /** キャンセルコード取得結果画面を閉じる */
    closeResult() {
      this.showCancelCodeResult = false;
    },

    /**
     * テーブル情報の更新を行う
     */
    async updateTable() {
      try {
        this.processing = true;
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        // キャンセルコード一覧取得のポーリング処理を行う
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.CNC,
              '/netApps/' + this.netAppId + '/systems/' + this.systemId + '/initialize',
              {
                use_cache: this.isUseCache
              }
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.CNC,
              '/netApps/' + this.netAppId + '/systems/' + this.systemId + '/initialize/requests/' + postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse && postApiResponse.data && postApiResponse.data.request_id ? '' : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (getApiResponse && getApiResponse.data && getApiResponse.data.status) {
              switch (getApiResponse.data.status) {
                case config.STATUS_CNC_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_CNC_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_CNC_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合は、一覧データ取得処理を行う
          this.menuResponse = await this.getMenuResponse();
          // responseが存在しない場合、処理終了
          if (!this.menuResponse) {
            return;
          }
          // キャンセルコード一覧の多言語変換リストの取得
          const MenuList = this.getCnci18nMenuList(this.menuResponse, this.systemId);
          this.originalCancelCodeList = MenuList;
          this.filteredCancelCodeList = MenuList;
          this.updateFilter();
          // 表示項目がない場合、非サポート画面を表示
          if (this.filteredCancelCodeList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * 一覧取得処理を行う関数
     * @returns 成功時は menus リストを返す（失敗時は null）
    */
    async getMenuResponse() {
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await api.getCall(
            config.CNC,
            '/netApps/' + this.netAppId + '/systems/' + this.systemId + '/menus'
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // menus が入っていれば成功とする
          return getApiResponse && getApiResponse.data && getApiResponse.data.menus ? '' : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
      // 処理失敗時は response が null になるので考慮
      // キャンセルコード一覧に空行表示されるため、i18n未実装のものは除外（menu_title有無で判定）
      return response ? response.data.menus?.filter(
        menu => this.$te(`cnc${this.systemId}.${menu.cancel_code_id}.menu_title`)
      ) : null;
    },

    /**
     * キャンセルコードの多言語変換の一覧取得
     * @param {Array} menuResponse メニュー一覧
     * @param {string} systemId システム ID
     */
    getCnci18nMenuList(menuResponse, systemId) {
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * @param {string} i18nCncMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nCncMenuKey, messageKey) => {
        const key = `${i18nCncMenuKey}.${messageKey}`;
        return this.$te(key) ? this.$t(key) : undefined;
      };

      // 多言語変換リストの取得
      return menuResponse.map(item => {
        const i18nCncMenuKey = `cnc${systemId}.${item.cancel_code_id}`;
        return {
          cancel_code_id: item.cancel_code_id,
          sort_id: item.sort_id,
          requirement_id: item.requirement_id,
          menu_title: convertI18nText(i18nCncMenuKey, 'menu_title'),
          menu_description: convertI18nText(i18nCncMenuKey, 'menu_description') || '',
        };
      }).sort((a, b) => {
        // sort_id(表示順) 要素でソートする
        return a.sort_id - b.sort_id;
      });
    },

    /**
     * その他のエラー
     */
    showUnexpectedError() {
      alert.showError(this, null, this.$t('individual.error_Initialize_communication_error'), () => { });
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result バリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 呼び出し時のコンポーネント内ガードの共通処理
     * @param vm 呼び出し元のオブジェクト(this)
     * @param from 遷移元
     */
    beforeRouteProcess(vm, from) {
      if (util.isFromSettingsPage(from.name) || util.isSameSystemPage(from)) {
        // 単位設定画面またはパスワード変更画面からの遷移時、または同じシステム内の子機能間への遷移の場合 trueを指定
        vm.isUseCache = true;
      } else {
        // 上記以外の場合、falseを指定
        vm.isUseCache = false;
      }
    }
  },

  created() {
    this.systemId = individualDiagnose.getCurrentSystemId(this);
  },

  mounted() {
    this.mountedLogic();
  }
};
